import { HGAnyRecord, HGCollection } from "@/common/types";
import RecordIntervalName from "./RecordIntervalName";
import { RecordMediaSection } from "./RecordMedia";
import { ExternalUrl } from "./ExternalUrl";
import { isValidElement, ReactNode, useState } from "react";
import { intervalNames } from "@/common/record";
import { collectionPath } from "@/common/urls";
import { collectionParentIds } from "@/common/utils";
import { useCollections } from "@/api/hooks";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { Button } from "../button";
import { Check, Copy } from "lucide-react";
import { useNavigate } from "react-router";

// Walks upward (outermost ancestor first) so nested collections can be shown as
// e.g. "Trips -> Japan 2024" rather than just the leaf name. Explicit links come
// first; a collection nested only through its title record still gets a path.
// `visited` is load-bearing: implicit edges can form a cycle.
function getCollectionPath(
  collection: HGCollection,
  byId: Map<string, HGCollection>,
): HGCollection[] {
  const path: HGCollection[] = [collection];
  const visited = new Set([collection.id]);
  let current = collection;
  for (;;) {
    const parentID = collectionParentIds(current)[0];
    if (!parentID) break;
    const parent = byId.get(parentID);
    if (!parent || visited.has(parent.id)) break;
    path.unshift(parent);
    visited.add(parent.id);
    current = parent;
  }
  return path;
}

interface RecordViewProps {
  record: HGAnyRecord;
  markdownEnabled?: boolean;
  showTitle?: boolean;
}

function CodeBlock({
  children,
  className,
}: {
  children: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className={className}>
        <code>{children}</code>
      </pre>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleCopy}
        className="absolute top-1 right-2 size-6 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copied ? <Check className="size-2" /> : <Copy className="size-2" />}
      </Button>
    </div>
  );
}

function InlineCode({ children }: { children: string }) {
  const handleClick = async () => {
    await navigator.clipboard.writeText(children);
  };

  return (
    <code onClick={handleClick} className="cursor-pointer">
      {children}
    </code>
  );
}

export default function RecordView({
  record,
  markdownEnabled = true,
  showTitle = false,
}: RecordViewProps) {
  const navigate = useNavigate();
  const recordCollections = useCollections(record.collection_id);
  const allCollections = useCollections();

  const collectionsById = new Map(allCollections.map((c) => [c.id, c]));
  // The collection this record heads, as opposed to the ones it belongs to.
  const titleCollection = record.title_collection_id
    ? collectionsById.get(record.title_collection_id)
    : undefined;

  return (
    <div className="h-full w-full flex flex-col gap-3">
      {showTitle && (
        <div className="flex shrink-0 flex-col gap-3">
          <h1
            className="text-[25px] font-semibold leading-[1.18] text-foreground"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            {record.title || "No title"}
          </h1>
          {"interval" in record && (
            <div className="font-mono text-[12px] text-muted-foreground tracking-[.01em]">
              <RecordIntervalName
                record={{ ...record, ...intervalNames(record) }}
              />
            </div>
          )}
          <div className="w-[40px] h-[2px] bg-primary" />
        </div>
      )}

      {(recordCollections.length > 0 || titleCollection) && (
        <div className="flex shrink-0 flex-wrap gap-1.5">
          {recordCollections.map((collection) => {
            const path = getCollectionPath(collection, collectionsById);
            return (
              <div
                key={collection.id}
                className="whitespace-nowrap rounded border border-border bg-card px-2 py-0.5 font-sans text-[10.5px] text-muted-foreground"
              >
                {path.map((col, i) => (
                  <span key={col.id}>
                    {i > 0 && (
                      <span className="mx-1 text-[13px] text-muted-foreground">
                        ›
                      </span>
                    )}
                    <span
                      className="cursor-pointer hover:text-foreground hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(collectionPath(col));
                      }}
                    >
                      {col.title}
                    </span>
                  </span>
                ))}
              </div>
            );
          })}

          {/* Set apart from the chips above, which say where this record is
              filed. This one says what it heads — the opposite direction. */}
          {titleCollection && (
            <div className="flex items-center gap-1.5 whitespace-nowrap rounded border border-primary/40 bg-primary/5 px-2 py-0.5 font-sans text-[10.5px] text-muted-foreground">
              <span
                aria-hidden
                className="h-[10px] w-[2px] shrink-0 rounded-full bg-primary"
              />
              {/* <span className="text-muted-foreground/80">Title of</span> */}
              <span
                className="cursor-pointer text-foreground hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(collectionPath(titleCollection));
                }}
              >
                {titleCollection.title}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Media, links and text scroll together as one region */}
      <div className="flex min-h-0 grow flex-col gap-3 overflow-auto">
        <RecordMediaSection recordId={record.id} />

        <ul>
          {record.url.map((urlInfo) => (
            <li key={urlInfo.url}>
              <ExternalUrl href={urlInfo.url} label={urlInfo.label} />
            </li>
          ))}
        </ul>

        {markdownEnabled ? (
          <div className="prose prose-sm dark:prose-invert max-w-none text-[15px] text-ink2 leading-[1.7] [&_p]:max-w-[60ch]">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              components={{
                pre: ({ children }) => {
                  if (
                    isValidElement<{
                      children?: ReactNode;
                      className?: string;
                    }>(children) &&
                    typeof children.props.children === "string"
                  ) {
                    return (
                      <CodeBlock className={children.props.className}>
                        {children.props.children}
                      </CodeBlock>
                    );
                  }
                  return <pre>{children}</pre>;
                },
                code: ({ node: _node, ...props }) => {
                  const { children } = props;
                  // Inline code doesn't have a parent pre element
                  if (
                    typeof children === "string" &&
                    !children.includes("\n")
                  ) {
                    return <InlineCode>{children}</InlineCode>;
                  }
                  return <code {...props}>{children}</code>;
                },
              }}
            >
              {record.content}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none text-[15px] text-ink2 leading-[1.7]">
            <div className="whitespace-pre-line">{record.content}</div>
          </div>
        )}
      </div>
    </div>
  );
}
